// #region Imports

/* Telestream */
import { ConnectionService } from '@/services/connection.service';
import { isHlsSupported, VideoController } from '@user-interface/glim-player';
import { sendEphemeralSnack, LEVELS } from './snacks.utils';

// #endregion Imports

export async function fetchMediaProperties(origin, videoPath, audioPaths, open) {
  const connection = ConnectionService.getHubConnection(origin, '/inspector', false);
  await connection.start();

  let properties = undefined;

  try {
    properties = await connection.invoke('GetMediaProperties', videoPath, audioPaths, open? open : false);
  }
  catch (e) {
    // Conform the error structure to how the back-end reports errors.
    properties = {
      containerProperties: [
        {
          name:  'Error',
          value: 'Unable to fetch the media properties of the media or associated sidecar audio tracks'
        }
      ]
    };
  }

  connection.stop();
  return properties;
}

export async function fetchVideoScopeGraticule(origin, videoPath, audioPaths, videoScope) {
  const connection = ConnectionService.getHubConnection(origin, '/inspector', false);
  await connection.start();

  const properties = await connection.invoke('GetVideoScopeGraticule', videoPath, audioPaths, videoScope);

  connection.stop();
  return properties;
}

export function generateController(element, manifestUrl, mediaProperties, quality) {
  if (!isHlsSupported()) {
    return undefined;
  }

  const controller = new VideoController(
    element,
    manifestUrl,
    mediaProperties,
    quality,
    {fragLoadingMaxRetry: 25, backBufferLength: 600}
  );

  controller.init();

  return controller;
}

const utf8Decoder = new TextDecoder('utf-8');

export function onHlsError(context, error) {
  if (
    (error.type === 'networkError') &&
    (error.details === 'fragLoadError') &&
    (error.response.code === 403)
  ) {
    let forbiddanceReason;
    try {
      const decoded = utf8Decoder.decode(error.networkDetails.response);
      forbiddanceReason = JSON.parse(decoded).forbiddanceReason;
    }

    catch { /* In case of JSON parse errors. */}

    switch (forbiddanceReason) {

    case 'NotLicensed':
      sendEphemeralSnack(context, LEVELS.ERROR, 'System is not licensed.');
      break;

    case 'Unauthorized':
      sendEphemeralSnack(context, LEVELS.ERROR, 'Not authorized to access this file.');
      break;

    case 'SessionLimitReached':
      sendEphemeralSnack(context, LEVELS.ERROR, 'Session limit reached.');
      break;

    case 'AdminRemoved':
      sendEphemeralSnack(context, LEVELS.ERROR, 'Session closed by administrator.');
      break;

    default:
      sendEphemeralSnack(context, LEVELS.ERROR, 'Unable to process media format.');
      break;
    }
  } else {
    console.error(error);
  }
}

// #region Image Detection Constants

const CONTAINER_TYPE_PROPERTY_NAME = 'container_type';
const IMAGE_FORMATS = [
  'gif',
  'jfif',
  'jpeg',
  'jpg',
  'png',
  'tiff'
];

// #endregion Image Detection Constants

export function isImage(mediaProperties) {
  const containerProperties = mediaProperties.containerProperties;
  if (!containerProperties) {
    return false;
  }

  const typeProperty = containerProperties.find(p => p.name === CONTAINER_TYPE_PROPERTY_NAME);
  if (!typeProperty) {
    return false;
  }

  const type = typeProperty.value
    .replaceAll('\'', '')
    .trim();

  return IMAGE_FORMATS.includes(type);
}

export function handleFatalNetworkError(data, controller) {
  if (data.fatal && data.type === 'networkError') {
    controller.hls.startLoad();
  }
}
